import {
  CLUB_MEMBER_SORT_KEYS,
  ClubService,
} from '@/api/services/club.service';
import type {
  IClubActivityParams,
  TClubActivityKind,
  TClubActivitySource,
} from '@/api/services/club.service';
import { PermissionType } from '@/enums/PermissionType';
import {
  HostToolError,
  type ICartadigitalAdminHostToolDeps,
  type IGpthelpTool,
} from '../tool-types';
import {
  getLimitArg,
  getOptionalBoolean,
  getOptionalEnum,
  getOptionalNumber,
  getOptionalString,
  getPageArg,
  normalizeInstantRange,
  RANGE_END_DESCRIPTION,
} from '../tool-args';
import { accessibleBranches } from '../tool-gate';
import { runToolApiCall } from '../tool-http';
import {
  ensureAnyPermissionOrThrow,
  ensurePermissionOrThrow,
} from '../tool-scope';
import {
  omitNullish,
  sanitizeClubActivityRow,
  sanitizeClubMember,
  sanitizeClubProgram,
} from '../sanitize';

const ensureClubManage = (deps: ICartadigitalAdminHostToolDeps) =>
  ensurePermissionOrThrow({
    deps,
    permission: PermissionType.ClubManage,
    message: 'No tiene permisos para administrar el club',
  });

const ensureClubMemberRead = (deps: ICartadigitalAdminHostToolDeps) =>
  ensureAnyPermissionOrThrow({
    deps,
    permissions: [PermissionType.ClubTopup, PermissionType.ClubManage],
    message: 'No tiene permisos para ver miembros del club',
  });

const CLUB_ACTIVITY_KINDS: TClubActivityKind[] = [
  'adjust',
  'topup',
  'redeem',
  'reversal',
];
const CLUB_ACTIVITY_SOURCES: TClubActivitySource[] = ['points', 'cash'];

/** The names of the store's branches the member works in, as the club page lists them. */
const clubBranchNames = (
  deps: ICartadigitalAdminHostToolDeps,
): Map<number, string> =>
  new Map(
    accessibleBranches(deps).map((branch): [number, string] => [
      branch.id,
      branch.name,
    ]),
  );

export const clubTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'club_summary',
      description:
        "Club program KPIs summary (the Club page's reports): members, balances, points liability, top-ups and the program rules (how many points each purchase earns, what they are worth, expiry, welcome points, top-up plans). Restaurant-wide (ignores the selected branch).",
      sideEffects: 'none',
      parameters: { type: 'object', additionalProperties: false, properties: {} },
    },
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageClub,
    async execute(_args, { deps, scope }) {
      ensureClubManage(deps);
      // The program's read needs ClubTopup or ClubManage, the summary's
      // ClubManage: whoever reads the summary reads the program too. The
      // rules ride along: when only they fail, the KPIs still answer.
      const [summary, program] = await Promise.allSettled([
        runToolApiCall(() => ClubService.getReportsSummary(scope.restaurantId)),
        ClubService.getProgram(scope.restaurantId),
      ]);
      if (summary.status === 'rejected') throw summary.reason;
      if (program.status === 'rejected') {
        console.warn('[assistant] club program unavailable', program.reason);
        // No `program` (null would mean the store has none).
        return { ...summary.value, programReadFailed: true };
      }
      return {
        ...summary.value,
        program: sanitizeClubProgram(program.value, clubBranchNames(deps)),
      };
    },
  },
  {
    definition: {
      name: 'club_findMembers',
      description:
        'Search or list club loyalty members (name, phone, points and cash balances, visits, last visit). Restaurant-wide. These are NOT saved customers (use customers_find for those).',
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          search: {
            type: 'string',
            description: 'Name, phone or member number.',
          },
          page: { type: 'number', minimum: 1, default: 1 },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 20 },
          sortBy: {
            type: 'string',
            enum: [...CLUB_MEMBER_SORT_KEYS],
            default: 'name',
            description: 'cashTotal = total balance (principal + bonus).',
          },
          sortDesc: { type: 'boolean', default: false },
        },
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) =>
      gate.hasRestaurantScope && (gate.canTopupClub || gate.canManageClub),
    async execute(args, { deps, scope }) {
      ensureClubMemberRead(deps);

      const search = getOptionalString(args, 'search');
      const page = getPageArg(args);
      const limit = getLimitArg(args, { def: 20, max: 50 });
      const sortBy =
        getOptionalEnum(args, 'sortBy', CLUB_MEMBER_SORT_KEYS) ?? 'name';
      const sortDesc = getOptionalBoolean(args, 'sortDesc') ?? false;

      const data = await runToolApiCall(() =>
        ClubService.getMembers(scope.restaurantId, {
          page,
          itemsPerPage: limit,
          search: search ?? undefined,
          sortBy: [sortBy],
          sortDesc: [sortDesc],
          mustSort: true,
        }),
      );

      return {
        total: data.total,
        page,
        results: (data.items ?? []).map(sanitizeClubMember),
      };
    },
  },
  {
    definition: {
      name: 'club_getMember',
      description:
        "One club member's full record (balances, contact, status) plus their recent activity (points and cash ledger entries, newest first). Pass activityCursor to page older entries.",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          memberId: { type: 'number' },
          includeActivity: { type: 'boolean', default: true },
          activityCursor: {
            type: 'string',
            description:
              'Opaque cursor from a previous result; pass it EXACTLY as received.',
          },
          activityFilter: {
            type: 'string',
            enum: ['adjust'],
            description: 'Only manual adjustments.',
          },
        },
        required: ['memberId'],
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) =>
      gate.hasRestaurantScope && (gate.canTopupClub || gate.canManageClub),
    async execute(args, { deps }) {
      ensureClubMemberRead(deps);

      const memberIdRaw = getOptionalNumber(args, 'memberId');
      if (memberIdRaw === null || memberIdRaw <= 0) {
        throw new HostToolError('INVALID_ARGS', 'Missing/invalid "memberId"', {
          memberId: args.memberId,
        });
      }
      const memberId = Math.floor(memberIdRaw);
      const includeActivity = getOptionalBoolean(args, 'includeActivity') ?? true;
      const activityCursor = getOptionalString(args, 'activityCursor');
      const activityFilter = getOptionalEnum(args, 'activityFilter', [
        'adjust',
      ] as const);

      // Tenant scoping is server-side (requireMember checks the member's
      // restaurant against the caller's roles); a foreign id yields 401/404.
      const [member, activity] = await runToolApiCall(() =>
        Promise.all([
          ClubService.getMember(memberId),
          includeActivity
            ? ClubService.getMemberActivity(
                memberId,
                activityCursor,
                activityFilter ?? undefined,
              )
            : Promise.resolve(null),
        ]),
      );

      if (!member) {
        throw new HostToolError('NOT_FOUND', 'Miembro no encontrado', { memberId });
      }

      const result: Record<string, unknown> = {
        member: sanitizeClubMember(member),
      };
      if (includeActivity && activity) {
        result.activity = omitNullish({
          results: (activity.items ?? []).map(sanitizeClubActivityRow),
          nextCursor: activity.nextCursor,
        });
      }
      return result;
    },
  },
  {
    definition: {
      name: 'club_getMovements',
      description:
        "Program-wide feed of EVERY club points/cash movement across ALL members, newest first, restaurant-wide (ignores the selected branch) - the Club page's Movimientos tab. Use for audit questions ('who adjusted points yesterday?', 'top-ups this week'). For ONE member's history use club_getMember instead. points/amount are signed deltas; balanceAfter is the member's per-ledger balance after the entry; rows carry orderId when tied to an order (follow up with orders_get).",
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          kind: {
            type: 'string',
            enum: ['adjust', 'topup', 'redeem', 'reversal'],
            description:
              'adjust = manual staff corrections; topup = cash top-ups incl. bonus credit; redeem = points redeemed / club cash spent on orders; reversal = reversals, revoked points and refund credits. OMIT to include EVERYTHING, notably automatic entries the named kinds never return: points earn, welcome and expire.',
          },
          source: { type: 'string', enum: ['points', 'cash'] },
          dateFrom: {
            type: 'string',
            description: 'Start date (yyyy-MM-dd, branch-local) or ISO instant.',
          },
          dateTo: { type: 'string', description: RANGE_END_DESCRIPTION },
          limit: { type: 'number', minimum: 1, maximum: 50, default: 30 },
          cursor: {
            type: 'string',
            description:
              'Opaque; pass nextCursor from the previous result EXACTLY as received, keeping ALL other filters identical; never fabricate one; restart without it when filters change.',
          },
        },
      },
    },
    policy: 'read',
    readsDinerText: true,
    isAvailable: (gate) => gate.hasRestaurantScope && gate.canManageClub,
    async execute(args, { deps, scope }) {
      ensureClubManage(deps);

      const kind = getOptionalEnum(args, 'kind', CLUB_ACTIVITY_KINDS);
      const source = getOptionalEnum(args, 'source', CLUB_ACTIVITY_SOURCES);
      const cursor = getOptionalString(args, 'cursor');
      const limit = getLimitArg(args, { def: 30, max: 50 });

      const { from, to } = normalizeInstantRange({
        obj: args,
        fromKey: 'dateFrom',
        toKey: 'dateTo',
        timeZone: scope.timeZone,
      });

      const params: IClubActivityParams = { limit };
      if (kind) params.kind = kind;
      if (source) params.source = source;
      if (cursor) params.cursor = cursor;
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;

      const data = await runToolApiCall(() =>
        ClubService.getActivity(scope.restaurantId, params),
      );

      return omitNullish({
        results: (data.items ?? []).map(sanitizeClubActivityRow),
        nextCursor: data.nextCursor,
      });
    },
  },
];
